import { asArray, cn } from "@/lib/utils";
import React from "react";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "../../ui/resizable";
import { ImperativePanelGroupHandle } from "react-resizable-panels";

// export const SideBySideOld: React.FC<
//   SideBySideProps & Omit<React.ComponentPropsWithoutRef<"div">, "children">
// > = ({
//   showChild: _showChild,
//   showParent: _showParent,
//   children,
//   parent: _parent,
//   child: _child,
//   ...props
// }) => {
//   const parent =
//     _parent ?? (children as any)?.parent ?? asArray(children as any)?.[0];
//   const child =
//     _child ?? (children as any)?.child ?? asArray(children as any)?.[1];
//   const showChild = _showChild ?? !!child;
//   const showParent = _showParent ?? !!parent;
//   // const [ratio, setRatio] = React.useState<number>(1);
//   // const resizeHandler = (
//   //   ev: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>,
//   // ) => {};
//   return (
//     <div
//       // style={{ "--ratio": `${ratio}fr` } as any}
//       {...props}
//       className={cn(
//         "transition-all duration-500 ease-out",
//         "w-full grid",
//         !showParent
//           ? "grid-cols-[0fr_1fr]"
//           : !showChild
//             ? "grid-cols-[1fr_0fr]"
//             : // : "grid-cols-[0fr_var(--ratio)] md:grid-cols-[1fr_var(--ratio)] lg:grid-cols-[1.5fr_var(--ratio)]",
//               "grid-cols-[0fr_1fr] md:grid-cols-[1fr_1fr] lg:grid-cols-[1.5fr_1fr]",
//         props?.className,
//       )}
//     >
//       <div
//         // ref={(el) => el?.focus()}
//         className={cn(
//           "pt-6",
//           "min-w-0 min-h-0",
//           showChild &&
//             "w-0 h-0 overflow-hidden invisible md:w-auto md:h-auto md:visible md:overflow-auto",
//         )}
//       >
//         {showParent && parent}
//       </div>
//       <div
//         className={cn(
//           "min-w-0 relative",
//           showParent &&
//             showChild &&
//             "md:pl-2 lg:pl-4 md:border-l-1 md:border-border md:shadow-[0px_0px_12px_0px_hsla(0,0%,0%,0.15)] z-20 overflow-hidden md:-mr-8 md:pb-2",
//         )}
//         style={{ clipPath: "inset(0px 0px 0px -10px)" }}
//       >
//         {/* <div
//           className={cn(
//             "absolute top-0 left-0 h-full z-30",
//             "cursor-col-resize w-1 after:content-[' '] after:absolute after:h-full after:hover:bg-border/90 after:active:bg-border after:w-0.5 after:active:w-1 after:hover:w-1",
//           )}
//           onMouseDown={resizeHandler}
//           onTouchStart={resizeHandler}
//         ></div> */}
//         <div className={cn("relative", showParent && showChild && "md:mr-8")}>
//           {showChild && child}
//         </div>
//       </div>
//     </div>
//   );
// };

export type SideBySideProps = {
  showChild?: boolean;
  showParent?: boolean;
  parent?: React.ReactNode;
  child?: React.ReactNode;
  children?:
    | { parent?: React.ReactNode; child?: React.ReactNode }
    | [React.ReactNode, React.ReactNode];
  defaultLayout?: [number, number];
  min?: [number, number];
  onCollapse?: () => void;
  onExpand?: () => void;
} & Omit<
  React.ComponentPropsWithoutRef<typeof ResizablePanelGroup>,
  "children" | "direction"
>;

export const SideBySide: React.FC<SideBySideProps> = ({
  showChild: _showChild,
  showParent: _showParent,
  children,
  parent: _parent,
  child: _child,
  defaultLayout: _defaultLayout,
  min,
  onCollapse,
  onExpand,
  ...props
}) => {
  const parent =
    _parent ?? (children as any)?.parent ?? asArray(children as any)?.[0];
  const child =
    _child ?? (children as any)?.child ?? asArray(children as any)?.[1];
  const showParent = _showParent ?? !!parent;
  const showChild = _showChild ?? !!child;
  const ref = React.useRef<ImperativePanelGroupHandle | null>(null);
  const last = React.useRef<number[] | undefined>(undefined);
  const defaultLayout = _defaultLayout ?? [50, 50];
  const cb = React.useCallback(
    (el: ImperativePanelGroupHandle | null) => {
      if (!el) return;
      if (!showParent) el.setLayout([0, 100]);
      else if (!showChild) el.setLayout([100, 0]);
      else el.setLayout(last.current ?? defaultLayout);
    },
    [ref, showParent, showChild, defaultLayout],
  );
  React.useEffect(() => {
    cb(ref.current);
  }, [ref.current, showChild, showParent]);
  const onLayout = (layout: number[]) => {
    if (min) {
      for (let i = 0; i < min.length; i++) {
        if (min[i] >= layout[i]) {
          return;
        }
      }
    }
    if (layout.find((l) => l >= 100)) {
      return;
    }
    last.current = layout;
  };
  const [dragging, setDragging] = React.useState(false);
  return (
    <ResizablePanelGroup
      direction="horizontal"
      {...props}
      ref={ref}
      onLayout={(layout) => {
        onLayout(layout);
        if (props?.onLayout) {
          props?.onLayout(layout);
        }
      }}
    >
      {
        <ResizablePanel
          defaultSize={defaultLayout?.[0]}
          minSize={showParent ? min?.[0] : undefined}
          collapsible={!!onExpand}
          onCollapse={showParent ? onExpand : undefined}
          className={cn(!dragging && "transition-all duration-500 ease-out")}
          style={{ overflow: "auto" }}
        >
          {showParent && parent}
        </ResizablePanel>
      }
      {showParent && showChild && (
        <ResizableHandle
          withHandle
          onDoubleClick={(ev) => {
            if (!ref.current) return;
            ref.current.setLayout(defaultLayout);
          }}
          className="cursor-ew-resize"
          onDragging={setDragging}
        />
      )}
      {
        <ResizablePanel
          defaultSize={showChild ? defaultLayout?.[1] : 1}
          minSize={showChild ? min?.[1] : undefined}
          collapsible={!!onCollapse}
          onCollapse={showChild ? onCollapse : undefined}
          className={cn(
            "shadow-[0px_0px_12px_0px_hsla(0,0%,0%,0.15)] dark:shadow-[0px_0px_12px_0px_hsl(var(--border)/0.75)]",
            !(showChild && showParent) &&
              "transition-all duration-500 ease-out",
          )}
          style={{ clipPath: "inset(0px 0px 0px -12px)", overflow: "auto" }}
        >
          {showChild && child}
        </ResizablePanel>
      }
    </ResizablePanelGroup>
  );
};
